import { useState } from "react";
import { X, Zap, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

interface ScheduleModalProps {
  open: boolean;
  onClose: () => void;
}

export default function ScheduleModal({ open, onClose }: ScheduleModalProps) {
  const [submitted, setSubmitted] = useState(false);

  if (!open) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />

      {/* Modal */}
      <div
        className="relative w-full max-w-lg rounded-2xl border border-crystal-purple/20 bg-[hsl(230,25%,12%)] p-6 md:p-8 animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-medium-gray hover:text-foreground transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {submitted ? (
          <div className="text-center py-8 animate-fade-in">
            <div className="w-16 h-16 rounded-full bg-crystal-success/20 flex items-center justify-center mx-auto mb-4">
              <Calendar className="w-8 h-8 text-crystal-success" />
            </div>
            <h3 className="text-2xl font-bold text-foreground mb-2">Thank You!</h3>
            <p className="text-medium-gray">
              We'll contact you within 24 hours to schedule your implementation call.
            </p>
          </div>
        ) : (
          <>
            <div className="text-center mb-6">
              <div className="inline-flex items-center gap-2 mb-3">
                <Zap className="w-5 h-5 text-crystal-purple" />
                <span className="text-sm font-semibold text-crystal-purple uppercase tracking-wide">
                  Arqera AI
                </span>
              </div>
              <h3 className="text-2xl font-bold text-foreground mb-2">
                Schedule Your Implementation Call
              </h3>
              <p className="text-medium-gray text-sm">
                See how Quality Crystal Ball can save your facility $1.9M+ annually
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-medium-gray mb-1.5">First Name *</label>
                  <Input
                    required
                    placeholder="John"
                    className="bg-crystal-card border-crystal-purple/20 text-foreground placeholder:text-medium-gray/50 focus-visible:ring-crystal-purple"
                  />
                </div>
                <div>
                  <label className="block text-sm text-medium-gray mb-1.5">Last Name *</label>
                  <Input
                    required
                    placeholder="Smith"
                    className="bg-crystal-card border-crystal-purple/20 text-foreground placeholder:text-medium-gray/50 focus-visible:ring-crystal-purple"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm text-medium-gray mb-1.5">Work Email *</label>
                <Input
                  type="email"
                  required
                  placeholder="john@manufacturing.com"
                  className="bg-crystal-card border-crystal-purple/20 text-foreground placeholder:text-medium-gray/50 focus-visible:ring-crystal-purple"
                />
              </div>
              <div>
                <label className="block text-sm text-medium-gray mb-1.5">Company *</label>
                <Input
                  required
                  placeholder="Acme Manufacturing"
                  className="bg-crystal-card border-crystal-purple/20 text-foreground placeholder:text-medium-gray/50 focus-visible:ring-crystal-purple"
                />
              </div>
              <div>
                <label className="block text-sm text-medium-gray mb-1.5">Phone</label>
                <Input
                  type="tel"
                  placeholder="+1 (555) 000-0000"
                  className="bg-crystal-card border-crystal-purple/20 text-foreground placeholder:text-medium-gray/50 focus-visible:ring-crystal-purple"
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-crystal-purple to-crystal-blue hover:opacity-90 text-foreground font-semibold h-12 text-base"
              >
                Request Implementation Call
              </Button>

              <p className="text-xs text-medium-gray text-center">
                No commitment required • Typical call is 30 minutes
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
